import { Quaternion, Vector3 } from "three";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";

export type RuntimeTransform = {
  p: [number, number, number];
  q: [number, number, number, number];
};

export type RuntimeJointRef = {
  modelUuid: string;
  jointId: number | string;
};

export type RuntimeBinding = {
  first: RuntimeJointRef;
  second: RuntimeJointRef;
};

export type RuntimePartRef = { type: string; name: string };

export type RuntimeModelDocument = {
  uuid: string;
  catalogModelId: number;
  transform: RuntimeTransform;
  partMaterials: Array<{ code: string; materialId: string }>;
};

export type RuntimeConfiguratorDocument = {
  uuid: string;
  transform: RuntimeTransform;
  childUuids: string[];
  mainConfig: Array<[string, RuntimePartRef]>;
  sideConfig: Array<[string, RuntimePartRef]>;
  materialConfig: Array<[string, { code: string; materialId: string }]>;
  bindings: RuntimeBinding[];
};

export type ConfiguratorRuntimeDocument = {
  version: 1;
  configuratorId: string;
  rootUuids: string[];
  models: RuntimeModelDocument[];
  configurators: RuntimeConfiguratorDocument[];
};

export function emptyConfiguratorRuntimeDocument(
  configuratorId: string,
): ConfiguratorRuntimeDocument {
  return {
    version: 1,
    configuratorId,
    rootUuids: [],
    models: [],
    configurators: [],
  };
}

export function captureConfiguratorRuntimeDocument(
  configuratorId: string,
  roots: readonly SugarModel[],
): ConfiguratorRuntimeDocument {
  const models = new Map<string, RuntimeModelDocument>();
  const configurators = new Map<string, RuntimeConfiguratorDocument>();

  const visit = (object: SugarModel): void => {
    if (object instanceof SugarModelConfigurator) {
      object.models.forEach(visit);
      configurators.set(object.uuid, {
        uuid: object.uuid,
        transform: worldTransform(object),
        childUuids: object.models.map((model) => model.uuid),
        mainConfig: partRefs(object.mainConfig),
        sideConfig: partRefs(object.sideConfig),
        materialConfig: [...object.materialConfig.entries()].map(
          ([key, entry]) => [
            key,
            {
              code: entry.code,
              materialId: String(entry.material?.id ?? entry.material?.uuid ?? ""),
            },
          ],
        ),
        bindings: captureBindings(object),
      });
      return;
    }

    const catalogModelId = Number(object.userData.info?.id ?? object.data?.id);
    if (!Number.isFinite(catalogModelId)) {
      throw new Error(`Runtime document model id missing: ${object.uuid}`);
    }
    models.set(object.uuid, {
      uuid: object.uuid,
      catalogModelId,
      transform: worldTransform(object),
      partMaterials:
        object.partMaterialGroups?.map((group) => ({
          code: group.code,
          materialId: String(group.material?.id ?? ""),
        })) ?? [],
    });
  };

  roots.forEach(visit);
  return JSON.parse(
    JSON.stringify({
      version: 1,
      configuratorId,
      rootUuids: roots.map((root) => root.uuid),
      models: [...models.values()],
      configurators: [...configurators.values()],
    }),
  ) as ConfiguratorRuntimeDocument;
}

function partRefs(
  config: ReadonlyMap<string, { type: string; name: string }>,
): Array<[string, RuntimePartRef]> {
  return [...config].map(([key, part]) => [
    key,
    { type: part.type, name: part.name },
  ]);
}

function worldTransform(model: SugarModel): RuntimeTransform {
  const position = model.getWorldPosition(new Vector3());
  const quaternion = model.getWorldQuaternion(new Quaternion());
  return {
    p: [position.x, position.y, position.z],
    q: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
  };
}

function captureBindings(
  configurator: SugarModelConfigurator,
): RuntimeBinding[] {
  const modelIds = new Set(configurator.models.map((model) => model.uuid));
  const seen = new Set<string>();
  const bindings: RuntimeBinding[] = [];
  for (const model of configurator.models) {
    for (const joint of model.joints ?? []) {
      const other = joint.bindedJoint;
      if (!other || !modelIds.has(other.model.uuid)) continue;
      const firstKey = `${model.uuid}:${joint.jointId}`;
      const secondKey = `${other.model.uuid}:${other.jointId}`;
      const key = [firstKey, secondKey].sort().join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      bindings.push({
        first: { modelUuid: model.uuid, jointId: joint.jointId },
        second: {
          modelUuid: other.model.uuid,
          jointId: other.jointId,
        },
      });
    }
  }
  return bindings;
}
